import _ from 'lodash';
import LangEnum from '../lang/LangEnum';
import { useTranslation } from '../modules/translation/TranslationContext';
import { useUpdateOrCreateColumnsPreference } from '../services/modules/userContextPreferences/userContextPreferences.mutations';
import { ColumnsPreferenceRecognizedLists } from '../services/modules/userContextPreferences/userContextPreferences.types';
import snackbarStore from '../stores/Snackbar.store';
import { useUserContextPreferences } from './useUserContextPreferences';

interface UseColumnsPreferenceActionsParams {
  list: ColumnsPreferenceRecognizedLists;
  visibleColumnKeys: string[];
}

const useColumnsPreferenceActions = ({
  list,
  visibleColumnKeys,
}: UseColumnsPreferenceActionsParams) => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();

  const { mutate, isPending: isSavingColumns } = useUpdateOrCreateColumnsPreference();
  const { getDefaultColumnsToList, hasFetchedUserPreferences } = useUserContextPreferences();
  const defaultColumns = getDefaultColumnsToList(list);

  const isSameAsDefault =
    !!defaultColumns && _.isEqual([...defaultColumns].sort(), [...visibleColumnKeys].sort());

  const canRestoreToDefaultColumns =
    hasFetchedUserPreferences && !!defaultColumns && !isSavingColumns && !isSameAsDefault;

  const canSaveColumns =
    hasFetchedUserPreferences && !isSavingColumns && (!defaultColumns || !isSameAsDefault);

  const saveColumns = () => {
    mutate(
      { list, defaultColumns: visibleColumnKeys },
      {
        onError: () => {
          handleMessage({
            type: 'error',
            message: translate(LangEnum.COLUMNS_ERROR_SAVING),
          });
        },
      },
    );
  };

  return {
    canSaveColumns,
    canRestoreToDefaultColumns,
    isSavingColumns,
    saveColumns,
  };
};

export default useColumnsPreferenceActions;
